import { Component, OnDestroy, OnInit, TemplateRef } from '@angular/core';
import { finalize, Subscription } from 'rxjs';
import {
  ProductionStockItem,
  ProductStockAdjustmentContext,
  WarehouseBatchStock,
} from '../../production-inventory.model';
import { ProductionInventoryService } from '../../services/production-inventory.service';
import { PaginatedResponse } from '../../../common/models/common.model';
import { RawMaterialService } from '../../../raw-material-inventory/raw-material.service';
import { RawMaterialWarehouse } from '../../../raw-material-inventory/raw-material.model';
import { SnackbarService } from '../../../services/snackbar.service';
import { SnackbarAlert, SnackbarAlertStatus, toNumberOrNull } from '../../../utils';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';

interface WarehouseBatchRow {
  /** The row this was flattened from. The adjustment needs its ids, not just its labels. */
  source: WarehouseBatchStock;
  packaging: string;
  warehouse_name: string;
  batch_number: string;
  mfg_date: string;
  exp_date: string;
  boxes_available: string;
  units_available: string;
  total_stock_cost: string;
}

interface GroupedWarehouseBatch {
  product_name: string;
  rows: WarehouseBatchRow[];
  total_cost: number;
}

@Component({
  selector: 'app-production-stock',
  standalone: false,
  templateUrl: './production-stock.component.html',
  styleUrl: './production-stock.component.scss',
})
export class ProductionStockComponent implements OnInit, OnDestroy {
  protected stockItems: ProductionStockItem[] = [];
  protected batchStockItems: WarehouseBatchStock[] = [];
  protected groupedBatchStock: GroupedWarehouseBatch[] = [];
  protected isDataLoading = false;
  protected isExporting = false;
  protected warehouses: RawMaterialWarehouse[] = [];
  protected selectedWarehouse: string | null = null;
  protected showBatchView = false;

  protected totalCount = 0;
  protected hasNextPage = false;
  protected hasPreviousPage = false;
  protected currentPage = 1;
  protected totalStockValue: number | null = null;

  protected adjustContext: ProductStockAdjustmentContext | null = null;
  protected historyBatch: WarehouseBatchStock | null = null;
  protected historyRefreshToken = 0;

  private adjustModalRef: NgbModalRef | null = null;
  private subscription: Subscription = new Subscription();

  constructor(
    private productionInventoryService: ProductionInventoryService,
    private rawMaterialService: RawMaterialService,
    private snackbarService: SnackbarService,
    private modalService: NgbModal
  ) {}

  ngOnInit(): void {
    this.loadWarehouses();
    this.loadStock();
  }

  /**
   * The confirmation needs the product's aggregate in this warehouse as well as the batch
   * balance, and the batch list does not carry it, so it is fetched when the form opens.
   * The adjustment still posts without it.
   */
  protected openAdjustModal(batch: WarehouseBatchStock, content: TemplateRef<any>): void {
    this.adjustContext = this.buildAdjustContext(batch, null);
    this.adjustModalRef = this.modalService.open(content, { size: 'lg', backdrop: 'static' });

    this.subscription.add(
      this.productionInventoryService.getProductionStocks({ warehouse: batch.warehouse, page_size: 100 }).subscribe({
        next: (response: PaginatedResponse<ProductionStockItem>) => {
          const aggregate = (response.results || []).find(item => item.product_packaging === batch.product_packaging);
          if (aggregate && this.adjustContext?.production_entry === batch.production_entry) {
            this.adjustContext = this.buildAdjustContext(batch, aggregate.units_available);
          }
        },
        // The aggregate only enriches the confirmation; the adjustment posts without it.
        error: () => undefined,
      })
    );
  }

  private buildAdjustContext(batch: WarehouseBatchStock, warehouseUnits: string | null): ProductStockAdjustmentContext {
    return {
      scope: 'warehouse',
      production_entry: batch.production_entry,
      batch_number: batch.batch_number,
      product_name: batch.product_name,
      packaging_label: `${batch.packing_size} ${batch.unit}${batch.packaging_type ? ' / ' + batch.packaging_type : ''}`,
      // The row already names the warehouse, so it is never inferred from the batch.
      container_id: batch.warehouse,
      container_name: batch.warehouse_name,
      nos: toNumberOrNull(batch.nos) ?? 0,
      unit: batch.unit,
      batch_units_available: batch.units_available,
      container_units_available: warehouseUnits,
    };
  }

  protected onAdjustmentSaved(): void {
    this.adjustModalRef?.close();
    this.adjustModalRef = null;
    this.adjustContext = null;

    // Both the batch balance and the warehouse aggregate moved, and this screen reads both.
    // A batch adjusted to zero drops off the list, which is how a row is emptied here.
    this.loadStock();
    this.historyRefreshToken += 1;
  }

  protected onAdjustCancelled(): void {
    this.adjustModalRef?.dismiss();
    this.adjustModalRef = null;
    this.adjustContext = null;
  }

  protected openHistoryModal(batch: WarehouseBatchStock, content: TemplateRef<any>): void {
    this.historyBatch = batch;
    this.modalService.open(content, { size: 'xl' });
  }

  private loadWarehouses(): void {
    this.rawMaterialService.get_warehouses().subscribe({
      next: (warehouses: RawMaterialWarehouse[]) => {
        this.warehouses = warehouses;
      },
    });
  }

  protected onWarehouseChange(): void {
    this.currentPage = 1;
    this.loadStock();
  }

  protected toggleBatchView(): void {
    this.showBatchView = !this.showBatchView;
    this.currentPage = 1;
    this.loadStock();
  }

  private loadStock(): void {
    this.isDataLoading = true;

    const params: any = { page: this.currentPage };
    if (this.selectedWarehouse) {
      params['warehouse'] = this.selectedWarehouse;
    }

    if (this.showBatchView) {
      this.subscription.add(
        this.productionInventoryService
          .getWarehouseBatchStocks(params)
          .pipe(finalize(() => (this.isDataLoading = false)))
          .subscribe({
            next: (response: PaginatedResponse<WarehouseBatchStock>) => {
              this.batchStockItems = response.results || [];
              this.groupedBatchStock = this.groupBatchStock(this.batchStockItems);
              this.totalCount = response.count;
              this.hasNextPage = !!response.next;
              this.hasPreviousPage = !!response.previous;
              this.totalStockValue = response.total_stock_value ?? null;
            },
          })
      );
    } else {
      this.subscription.add(
        this.productionInventoryService
          .getProductionStocks(params)
          .pipe(finalize(() => (this.isDataLoading = false)))
          .subscribe({
            next: (response: PaginatedResponse<ProductionStockItem>) => {
              this.stockItems = response.results || [];
              this.totalCount = response.count;
              this.hasNextPage = !!response.next;
              this.hasPreviousPage = !!response.previous;
              this.totalStockValue = response.total_stock_value ?? null;
            },
          })
      );
    }
  }

  private groupBatchStock(items: WarehouseBatchStock[]): GroupedWarehouseBatch[] {
    const grouped = new Map<string, WarehouseBatchRow[]>();

    for (const item of items) {
      const row: WarehouseBatchRow = {
        source: item,
        packaging: `${item.packing_size} ${item.unit}${item.packaging_type ? ' / ' + item.packaging_type : ''}`,
        warehouse_name: item.warehouse_name,
        batch_number: item.batch_number || '-',
        mfg_date: item.manufacturing_date || '-',
        exp_date: item.expiry_date || '-',
        boxes_available: item.boxes_available,
        units_available: item.units_available,
        total_stock_cost: item.total_stock_cost,
      };

      if (!grouped.has(item.product_name)) {
        grouped.set(item.product_name, []);
      }
      grouped.get(item.product_name)!.push(row);
    }

    return Array.from(grouped.entries()).map(([product_name, rows]) => ({
      product_name,
      rows,
      total_cost: rows.reduce((sum, r) => sum + (parseFloat(r.total_stock_cost) || 0), 0),
    }));
  }

  protected onPageChange(page: number): void {
    this.currentPage = page;
    this.loadStock();
  }

  protected exportStock(): void {
    this.isExporting = true;

    const params: any = {};
    if (this.selectedWarehouse) {
      params.warehouse = this.selectedWarehouse;
    }

    this.productionInventoryService
      .exportWarehouseStock(params)
      .pipe(finalize(() => (this.isExporting = false)))
      .subscribe({
        next: response => {
          const blob = response.body!;
          const disposition = response.headers.get('Content-Disposition') ?? '';
          const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          const filename =
            match?.[1]?.replace(/['"]/g, '') ||
            `Finish_Goods_Stock_Report_${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.xlsx`;

          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = filename;
          a.click();
          URL.revokeObjectURL(url);
        },
        error: () => {
          this.snackbarService.show(SnackbarAlert.SOMETHING_WENT_WRONG, SnackbarAlertStatus.ERROR);
        },
      });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.adjustModalRef?.dismiss();
  }
}
